import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { useTableSort, SortTh, type SortValue } from './dashboard/useTableSort';
type ChartType = 'table' | 'bar' | 'pie' | 'line';

export interface ChartConfig {
  type: ChartType;
  xKey?: string;
  yKey?: string;
  nameKey?: string;
  valueKey?: string;
  columns?: string[];
  title?: string;
}

// A chart layout is either a single ChartConfig (the common case) or an
// ordered array rendered side-by-side in a responsive grid (used when the
// user explicitly asked for multiple visuals of the same SQL result).
export type ChartLayout = ChartConfig | ChartConfig[];

interface Props {
  columns: string[];
  rows: Record<string, unknown>[];
  config: ChartLayout;
  // Map of static SQL alias → resolved display label (e.g. "sales_m3" →
  // "february_2026"). Server resolves it against today's date on every
  // run; client uses it purely to rename headers / axis labels. Empty
  // when the report doesn't declare dynamic-named columns.
  columnDisplay?: Record<string, string>;
}

// The views below each draw ONE chart: the default export splits a multi-chart
// layout into its configs before handing them over.
interface SingleChartProps extends Omit<Props, 'config'> {
  config: ChartConfig;
}

// Platform-default primary color, matching apps/web/src/pages/ProjectManagementPage.tsx
// (project_config theme default). Used when no per-project override is set in
// the auth store. Charts read the active project's theme_primary_color so the
// visual identity matches the rest of the dashboard.
const DEFAULT_PRIMARY = '#1B3A54';

// Companion palette used when a chart needs more than one colour (currently
// only the pie chart). First entry is filled at render time from the project's
// theme so the dominant slice matches the rest of the UI.
const PIE_PALETTE_REST = [
  '#16a34a', '#f97316', '#dc2626', '#9333ea',
  '#0891b2', '#ca8a04', '#db2777', '#65a30d', '#0ea5e9',
];

// Exported so the CSV download in CustomReportPanel renders every cell exactly
// as the on-screen table does — a second local copy would silently drift.
export const formatCell = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const toNumber = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// A cell's sort value. pg returns NUMERIC / BIGINT as strings, so numeric-looking
// strings compare as numbers; everything else compares as the text shown.
const sortValueOf = (v: unknown): SortValue => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(v)) return Number(v);
  return formatCell(v);
};

const TableView = ({ columns, rows, config, columnDisplay }: SingleChartProps) => {
  const cols = config.columns?.length ? config.columns : columns;
  const label = (c: string): string => columnDisplay?.[c] ?? c;
  const sort = useTableSort(
    rows,
    Object.fromEntries(cols.map((c) => [c, (row: Record<string, unknown>) => sortValueOf(row[c])])),
  );
  return (
    <div className="overflow-auto rounded border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
          <tr>
            {cols.map((c) => (
              <SortTh key={c} label={label(c)} sortKey={c} sort={sort} className="px-3 py-2 font-medium" title={c} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td className="px-3 py-4 text-gray-500" colSpan={cols.length || 1}>
                No rows.
              </td>
            </tr>
          )}
          {sort.sorted.map((row, i) => (
            <tr key={i} className="border-t border-gray-100">
              {cols.map((c) => (
                <td key={c} className="px-3 py-2 text-gray-800">
                  {formatCell(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface ChartViewProps extends SingleChartProps {
  primaryColor: string;
}

const BarView = ({ rows, config, primaryColor }: ChartViewProps) => {
  if (!config.xKey || !config.yKey) {
    return <div className="text-sm text-red-600">Bar chart requires xKey and yKey.</div>;
  }
  const data = rows.map((r) => ({ ...r, [config.yKey!]: toNumber(r[config.yKey!]) }));
  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={config.xKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey={config.yKey} fill={primaryColor} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const LineView = ({ rows, config, primaryColor }: ChartViewProps) => {
  if (!config.xKey || !config.yKey) {
    return <div className="text-sm text-red-600">Line chart requires xKey and yKey.</div>;
  }
  const data = rows.map((r) => ({ ...r, [config.yKey!]: toNumber(r[config.yKey!]) }));
  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={config.xKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey={config.yKey} stroke={primaryColor} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const PieView = ({ rows, config, primaryColor }: ChartViewProps) => {
  const nameKey = config.nameKey ?? config.xKey;
  const valueKey = config.valueKey ?? config.yKey;
  if (!nameKey || !valueKey) {
    return <div className="text-sm text-red-600">Pie chart requires nameKey and valueKey.</div>;
  }
  const data = rows.map((r) => ({
    name: formatCell(r[nameKey]),
    value: toNumber(r[valueKey]),
  }));
  const palette = [primaryColor, ...PIE_PALETTE_REST];
  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            outerRadius={120}
            label={(entry) => entry.name}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={palette[i % palette.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// Render a single chart (table/bar/pie/line) for one config. Used both
// directly (single-chart reports) and inside the array dispatcher below.
function SingleChartView({ columns, rows, config, primaryColor, columnDisplay }: ChartViewProps) {
  if (config.type === 'bar') return <BarView columns={columns} rows={rows} config={config} primaryColor={primaryColor} columnDisplay={columnDisplay} />;
  if (config.type === 'line') return <LineView columns={columns} rows={rows} config={config} primaryColor={primaryColor} columnDisplay={columnDisplay} />;
  if (config.type === 'pie') return <PieView columns={columns} rows={rows} config={config} primaryColor={primaryColor} columnDisplay={columnDisplay} />;
  return <TableView columns={columns} rows={rows} config={config} columnDisplay={columnDisplay} />;
}

export default function DynamicReportChart({ columns, rows, config, columnDisplay }: Props) {
  // Platform-standard primary colour — fixed across projects so the visual
  // identity of every chart matches modal headers, panel chrome, etc.
  // Per-project theme colours are reserved for branding surfaces (e.g. the
  // mailer's email header), not the in-app dashboards.
  const primaryColor = DEFAULT_PRIMARY;

  // Multi-chart layout: render each in a responsive grid (1 col on
  // narrow screens, 2 cols from `lg` up). Each cell carries the chart's
  // own title so the user can tell them apart.
  if (Array.isArray(config)) {
    if (config.length === 0) {
      return <div className="text-sm text-gray-500">No charts configured.</div>;
    }
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {config.map((c, i) => (
          <div key={i} className="rounded-md border border-gray-200 bg-white p-3">
            {c.title && (
              <div className="mb-2 text-sm font-medium text-gray-800">{c.title}</div>
            )}
            <SingleChartView columns={columns} rows={rows} config={c} primaryColor={primaryColor} columnDisplay={columnDisplay} />
          </div>
        ))}
      </div>
    );
  }
  return <SingleChartView columns={columns} rows={rows} config={config} primaryColor={primaryColor} columnDisplay={columnDisplay} />;
}
